import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { HttpClient } from '@angular/common/http';
import { debounceTime, switchMap, catchError, map, filter } from 'rxjs/operators';
import { Observable, of, forkJoin } from 'rxjs';
import { backendEndPointToken } from '../../app.config';
import { Patient } from 'fhir/r4';
import { PatientNamePipe } from '../pipes/patient-name.pipe';
import { PatientAgePipe } from '../pipes/patient-age.pipe';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-patient-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    MatFormFieldModule,
    PatientNamePipe,
    PatientAgePipe
  ],
  templateUrl: './patient-filter.component.html',
  styleUrls: ['./patient-filter.component.scss']
})
export class PatientFilterComponent implements OnInit {
  searchControl = new FormControl('');
  filteredPatients$: Observable<Patient[]> = of([]);
  isLoading = false;

  private http = inject(HttpClient);
  private backendEndPoint = inject(backendEndPointToken);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
       const patientId = params['patientId'];
       if (patientId) {
         // If we don't have the patient object in the control, fetch it
         const currentVal = this.searchControl.value as any;
         if (!currentVal || currentVal.id !== patientId) {
            this.isLoading = true;
            this.http.get<Patient>(`${this.backendEndPoint}/Patient/${patientId}`).subscribe({
               next: (patient) => {
                   this.isLoading = false;
                   this.searchControl.setValue(patient as any, { emitEvent: false });
               },
               error: () => {
                   this.isLoading = false;
               }
            });
         }
       } else if (this.searchControl.value) {
           this.searchControl.setValue('', { emitEvent: false });
       }
    });

    this.filteredPatients$ = this.searchControl.valueChanges.pipe(
      debounceTime(300),
      filter(value => typeof value === 'string' || value === null),
      switchMap((value: string | null) => {
        const query = (value || '').trim();
        if (query.length < 2) {
          return of([]);
        }
        this.isLoading = true;
        
        // Search by name
        const nameReq = this.http.get<any>(`${this.backendEndPoint}/Patient?name=${encodeURIComponent(query)}`).pipe(catchError(() => of({ entry: [] })));
        // Search by custom identifier
        const idReq = this.http.get<any>(`${this.backendEndPoint}/Patient?identifier=https://elikita-server.daalitech.com/fhir/identifier/pid|${encodeURIComponent(query)}`).pipe(catchError(() => of({ entry: [] })));

        return forkJoin([nameReq, idReq]).pipe(
          map(([nameBundle, idBundle]) => {
            this.isLoading = false;
            const allEntries = [...(nameBundle?.entry || []), ...(idBundle?.entry || [])];
            
            // Deduplicate by Patient ID
            const uniquePatients = new Map<string, Patient>();
            allEntries.forEach(e => {
               if (e.resource && e.resource.id) {
                   uniquePatients.set(e.resource.id, e.resource as Patient);
               }
            });
            return Array.from(uniquePatients.values());
          }),
          catchError(() => {
            this.isLoading = false;
            return of([]);
          })
        );
      })
    );
  }

  displayFn(patient: Patient | null | string): string {
    if (!patient) return '';
    if (typeof patient === 'string') return patient;
    const name = patient.name?.[0];
    if (!name) return 'Unknown Patient';
    const given = name.given?.join(' ') || '';
    const family = name.family || '';
    return `${given} ${family}`.trim();
  }

  getPatientPid(patient: Patient): string {
    const identifier = patient.identifier?.find(i => i.system === 'https://elikita-server.daalitech.com/fhir/identifier/pid');
    return identifier?.value || patient.id || 'N/A';
  }

  onPatientSelected(event: any) {
    const patient: Patient = event.option.value;
    if (patient && patient.id) {
      // Update query parameters in the current route
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { patientId: patient.id },
        queryParamsHandling: 'merge'
      });
    }
  }

  clearSearch() {
    this.searchControl.setValue('');
    // Remove patientId from query params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { patientId: null },
      queryParamsHandling: 'merge'
    });
  }
}
